import React from 'react';
import { Location } from '@gatsbyjs/reach-router';

import PropTypes from 'prop-types';
import { ImageProps } from '../../../prop-types';
import { useSiteConfig } from '../../../hooks/site-config';
import { buildQueryString } from '../../../utils/query-params';

const isPreview = process.env.PREVIEW === 'true';

const { type: OG_IMAGE_TYPE, ...OG_IMAGE_CONFIG } = {
  // For Contentful Image API, more info here: https://www.contentful.com/developers/docs/references/images-api/
  w: 1200,
  h: 630,
  fm: 'jpg',
  f: 'faces',
  q: 80,
  fit: 'fill',
  type: 'image/jpeg', // "type" is not used in the query string for Contentful Image API but is used for the meta tag
};

export const OG_IMAGE_QUERY_PARAMS = buildQueryString(OG_IMAGE_CONFIG); // builds the query string without the "type" key

const MetaTags = ({
  siteName,
  metaImage,
  description,
  pageTitle,
  deindexPage,
  contentfulId,
  canonicalTag,
  redactPixels = false,
}) => {
  const { twitterHandle } = useSiteConfig();

  return (
    <Location>
      {() => (
        <>
          <title>{pageTitle}</title>
          <meta name="title" content={pageTitle} />
          <meta name="description" content={description} />
          {deindexPage || isPreview ? (
            <meta name="robots" content="noindex, nofollow" />
          ) : null}
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={description} />
          <meta property="og:url" content={canonicalTag} />
          <meta property="og:site_name" content={siteName} />
          <meta property="og:type" content="article" />
          <meta name="twitter:site" content={twitterHandle} />
          <meta name="twitter:creator" content={twitterHandle} />
          <meta name="entryId" content={contentfulId} />
          <link rel="canonical" href={canonicalTag} />
          <meta name="redactPixels" content={String(redactPixels)} />
          {metaImage && <MetaImage metaImage={metaImage} />}
        </>
      )}
    </Location>
  );
};

const MetaImage = ({ metaImage }) => (
  <>
    <meta
      property="og:image"
      content={`https:${metaImage?.file?.url}${OG_IMAGE_QUERY_PARAMS}`}
    />
    <meta property="og:image:alt" content={metaImage.description} />
    <meta property="og:image:type" content={OG_IMAGE_TYPE} />
    {metaImage && metaImage.file.details && (
      <meta property="og:image:width" content={OG_IMAGE_CONFIG.w} />
    )}
    {metaImage && metaImage.file.details && (
      <meta property="og:image:height" content={OG_IMAGE_CONFIG.h} />
    )}
  </>
);

MetaTags.propTypes = {
  siteName: PropTypes.string,
  metaImage: ImageProps,
  description: PropTypes.string,
  pageTitle: PropTypes.string,
  deindexPage: PropTypes.bool,
  contentfulId: PropTypes.string,
  canonicalTag: PropTypes.string,
  redactPixels: PropTypes.bool,
};

MetaImage.propTypes = {
  metaImage: PropTypes.object,
};

export default MetaTags;
